import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';
import { useSelector, useDispatch } from 'react-redux';
import i18next from 'i18next';
import Replies from '../../containers/RepliesContainer/RepliesContainer';

import './RepliesCollapse.css';
import IconButton from '../IconButton/IconButton';
import RepliesLoadingAnimation from '../RepliesLoadingAnimation/RepliesLoadingAnimation';
import { loadMoreChildrenCommentsRequest } from '../../store/actions/comments/commentsActions';
import { COMMENTS_CONTEXT_SOCIAL_FEED } from '../../utils/constants/commentConstants';
import { getCommentsContext } from '../../store/selectors/settings/settingsSelectors';

// Replies collapse wraps replies in a container that will animate open/shut.
// This component is designed to wrap all replies, passing a false hasRepliesCollapse prop will just render a normal, none-collapse reply list.
// The collapse is just a wrapper and both collapse and non-collapse reply render using the same Replies component.
// Two tricky parts:
// 1. Dynamic height (and not just a rediculously large max-height that leads to a delay in the reverse animation).
//    So this is done by checking the scroll height of the content and then setting the appropriate max-height.
//    This also needs a resize function as width obviously affects content height.
// 2. The trigger element needs to live (completely) seperately in the DOM so can't be in this component.
//    This involved passing through a prop that actually controls the collapsed state and then reacting to changes to that prop.
//    (whilst this component doesn't care where the prop comes from, it is controlled in the closest shared parent).
const RepliesCollapse = ({ id, hasRepliesCollapse, repliesCollapsed }) => {
  const containerRef = useRef(); // container elemenent (we check the scroll height of this for determining maxheight).
  const [maxHeight, setMaxHeight] = useState(0); // used for inline max height (that's how we do dynamic height).
  const mutationObserverConfig = {
    attributes: false,
    childList: true,
    subtree: true
  };
  const comment = useSelector((state) =>
    state.commenting.comments.find((item) => item.id === id)
  );

  const commentContext = useSelector((state) => getCommentsContext(state));
  const dispatch = useDispatch();

  const getTimestamp = () => {
    const { children } = comment;
    // Gets newest comment timestamp. Children comments are sorted by old to new
    const filteredComments = children.filter(
      (item) => !item.recentlyAddedByUser
    );
    return filteredComments[filteredComments.length - 1].created_at_integer;
  };

  const handleOnLoadMore = () => {
    const { content_id: contentId } = comment;
    const timestamp = getTimestamp();
    dispatch(loadMoreChildrenCommentsRequest(contentId, timestamp, id));
  };
  // used to monitor changes in child DOM so we can rerun height check.
  let mutationObserver;

  // will return the current scrollHeight for content.
  const getRefHeight = () => {
    const currentRef = containerRef && containerRef.current;
    const currentRefHeight = currentRef && currentRef.scrollHeight;
    // When the scrollHeight is calculated, it does not account for the change in
    // scaling of the comments due to them being animated. This means we need to
    // adjust our height by the amount the scaling was reduced by. In this case
    // scaling is reduced from 100% to 80% meaning a reduction of 20% (See CommentWrapper.css)
    // Here we are reversing the reduction to get back to the original amount if everything
    // was rendered at a scale of 100%
    const adjustedHeight = (currentRefHeight / 80) * 100;
    return Number.isNaN(parseInt(currentRefHeight, 10))
      ? 'none'
      : adjustedHeight;
  };

  // process height will set the maxHeight state variable with the current scroll height for non-collapsed content or 0 for collapsed replies.
  const processHeight = () => {
    const newHeight = repliesCollapsed ? 0 : getRefHeight();
    if (maxHeight !== newHeight) {
      setMaxHeight(newHeight);
    }
  };
  // function called whenever the DOM inside the collapse updates (reply added, comment input control becomes visible etc.).
  const mutationObserverCallBack = () => {
    processHeight();
  };

  // resize only called on non-collapsed replies so doesn't have to worry about repliesCollapsed status.
  const handleResize = () => {
    const newHeight = getRefHeight();
    setMaxHeight(newHeight);
  };

  // name the debouncedResize so we can remove later.
  const debouncedResize = debounce(handleResize, 100);

  // Function to setup mutation observers on the collapse.
  const observeMutations = (isAdding) => {
    const currentRef = containerRef && containerRef.current; // Options for the observer (which mutations to observe).
    // always remove
    if (mutationObserver) {
      mutationObserver.disconnect();
    }
    // add if required
    if (isAdding === true && currentRef) {
      mutationObserver = new MutationObserver((mutations) => {
        mutations.forEach(() => {
          // function called whenever the DOM inside the collapse updates (reply added, comment input control becomes visible etc.).
          mutationObserverCallBack();
        });
      });
      mutationObserver.observe(currentRef, mutationObserverConfig);
    }
  };

  // mount/dismount functions
  useEffect(() => {
    return () => {
      // dismount
      if (mutationObserver) {
        mutationObserver.disconnect();
      }
      window.removeEventListener('resize', debouncedResize);
    };
  }, []);

  // effect when repliesCollapsed prop changes.
  useEffect(() => {
    // I think it is more efficient to add/remove resize event handlers here (not in the [] effect) as no point having a listener on collapsed replies.
    if (!repliesCollapsed) {
      observeMutations(true);
      window.removeEventListener('resize', debouncedResize);
      window.addEventListener('resize', debouncedResize);
    } else {
      observeMutations(false);
      window.removeEventListener('resize', debouncedResize);
    }
    processHeight();
    return () => {
      if (mutationObserver) {
        mutationObserver.disconnect();
      }
      window.removeEventListener('resize', debouncedResize);
    };
  }, [repliesCollapsed]);

  /**
   * Calculates the number of animations to show during a load more event.
   * @param {Number} totalCount total number of comment left to load
   * @param {Number} childrenCount total number children comments already fetched
   */
  const calculateLoadMoreAnimationCount = (totalCount, childrenCount) => {
    const difference = totalCount - childrenCount;
    if (difference >= 10) {
      return 10;
    }
    return difference;
  };

  const renderLoadingAnimation = () => {
    const {
      childLoading: { isLoadingMore, loadMoreError, totalCount }
    } = comment;
    if (isLoadingMore && !loadMoreError) {
      const animationCount = calculateLoadMoreAnimationCount(
        totalCount,
        comment.children.length
      );
      return (
        <>
          {Array.from(Array(animationCount)).map(() => (
            <RepliesLoadingAnimation />
          ))}
        </>
      );
    }
    return null;
  };

  const renderLoadMoreCommentsError = () => {
    const {
      childLoading: { loadMoreError }
    } = comment;
    return (
      loadMoreError && (
        <p styleName="child-load-more-error">{i18next.t('postingError')}</p>
      )
    );
  };

  const renderLoadMoreButton = () => {
    const {
      childLoading: { isLoadingMore, isLastPage }
    } = comment;

    const containerStyle = repliesCollapsed
      ? 'view-more-comments-container-inactive'
      : 'view-more-comments-container-active';
    return (
      !isLoadingMore &&
      !isLastPage &&
      commentContext !== COMMENTS_CONTEXT_SOCIAL_FEED && (
        // This testID is used to check the order of components
        <div testID="reply-component" styleName={containerStyle}>
          <IconButton
            testID="replies_view_more_comments_button"
            onClick={handleOnLoadMore}
            label={i18next.t('viewMoreComments')}
          />
          {renderLoadMoreCommentsError()}
        </div>
      )
    );
  };

  // since there was some conditional logic in the render it's easier to abstract to a function.
  const renderCollapse = () => {
    if (!id) {
      return null;
    }
    // why bother passing repliesCollapsed/hasRepliesCollapse to the Replies component? It is purely so that we can do animation on the replies themselves as they collapsed.
    if (hasRepliesCollapse) {
      return (
        <div
          className={
            repliesCollapsed ? 'collapse-active' : 'collapsed-inactive'
          }
          styleName={
            repliesCollapsed
              ? 'collapse-container-inactive'
              : 'collapse-container-active'
          }
          ref={containerRef}
          style={{ maxHeight }}
        >
          <Replies
            key={id}
            id={id}
            repliesCollapsed={repliesCollapsed}
            hasRepliesCollapse
            renderLoadingAnimation={renderLoadingAnimation}
            renderLoadMoreButton={renderLoadMoreButton}
          />
        </div>
      );
    }
    return <Replies id={id} />;
  };
  return renderCollapse();
};

RepliesCollapse.propTypes = {
  id: PropTypes.number,
  hasRepliesCollapse: PropTypes.bool,
  repliesCollapsed: PropTypes.bool,
  isLoadingMore: PropTypes.bool,
  loadMoreError: PropTypes.bool,
  isLastPage: PropTypes.bool
};

RepliesCollapse.defaultProps = {
  id: null,
  hasRepliesCollapse: false,
  repliesCollapsed: false,
  isLoadingMore: false,
  loadMoreError: false,
  isLastPage: true
};

export default RepliesCollapse;
